import axios from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasRelatedPurchaseOrders } from '@/features/lot-sizing/has-related-purchase-orders';
import { useLotSizingAddLineForm } from '@/features/lot-sizing/hooks/use-lot-sizing-add-line-form';
import { useLotSizingPurchaseOrderDraft } from '@/features/lot-sizing/lot-sizing-purchase-order-draft-context';
import type { CreateLotSizingLine } from '@/features/lot-sizing/schema/lot-sizing-add-line-schema';
import { apiValidationErrorHandler } from '@/lib/api-validation-error-handler';
import { useCreateLotSizingLine } from '@/services/apis/mutations/lot-sizing/use-create-lot-sizing-line';
import { useGetLotSizing } from '@/services/apis/queries/lot-sizing/use-get-lot-sizing';

export function useLotSizingAddLineDialog(lotSizingId: string) {
  const { t } = useTranslation('lotSizing');
  const [open, setOpen] = useState(false);
  const { control, handleSubmit, setError, reset } =
    useLotSizingAddLineForm(lotSizingId);
  const { data: lotSizing } = useGetLotSizing(lotSizingId);
  const lotSizingHasRelatedPurchaseOrders = hasRelatedPurchaseOrders(lotSizing);
  const { addDraftLine, hasDraftAddedProduct } =
    useLotSizingPurchaseOrderDraft();
  const { mutate: mutateCreateLine, isPending: isCreatingLine } =
    useCreateLotSizingLine();

  const handleCreateLineError = (error: unknown) => {
    apiValidationErrorHandler(setError)(error);

    const message = axios.isAxiosError(error)
      ? (error.response?.data?.message ?? error.response?.data?.detail)
      : null;

    if (!message) {
      return;
    }

    setError('productId', {
      type: 'server',
      message,
    });
  };

  const onSubmit = (data: CreateLotSizingLine) => {
    if (
      lotSizingHasRelatedPurchaseOrders &&
      hasDraftAddedProduct(data.productId)
    ) {
      setError('productId', {
        type: 'manual',
        message: t('This product is already in this lot sizing.'),
      });
      return;
    }

    mutateCreateLine(data, {
      onSuccess: (line) => {
        if (lotSizingHasRelatedPurchaseOrders && line !== null) {
          addDraftLine(line);
        }
        reset();
        setOpen(false);
      },
      onError: handleCreateLineError,
    });
  };

  const onOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setTimeout(() => reset(), 100);
    }
  };

  return {
    open,
    onOpenChange,
    control,
    handleSubmit,
    onSubmit,
    isPending: isCreatingLine,
  };
}
